import React from 'react';
import Typography from '@mui/material/Typography';
import {useDropzone} from 'react-dropzone';
import firebase from '../utilities/firebase.js';
import CircularProgress from '@mui/material/CircularProgress';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Preview from './Preview.js';
import Store from '../utilities/store.js';
import IconButton from '@mui/material/IconButton';
import { Add, Link, Search, OpenInNew, ContentCopy, Sms } from '@mui/icons-material';
import { StateManager } from '../utilities/stateManager.js';
import constants from '../utilities/constants';

import { v4 as uuidv4 } from 'uuid';
import { TextField } from '@mui/material';
import RequestManager from '../utilities/requestManager.js';


export default function PaymentLine(props) {
  const { reference, customer = {}, label = "", title = "", type ="", thumbnail = "", test = false, enable = false, revenue, deposits } = props;
  let {items = []} = props;
  const devtype = process.env.NODE_ENV === "development" ? "dev" : "prod";
  if(items.length <= 0){
    items = [{desc: "Invoice Amount", amount: revenue}, {desc: "Previous Deposits", amount: -deposits }];
  }

  const originalAmount = items.reduce((a,c) => a + (c.amount || 0), 0);
  const [amount, setAmount] = React.useState(originalAmount);
  const [inputValue, setInputValue] = React.useState(String(originalAmount));
  StateManager.setPaymentLineAmount = setAmount;
  const [activeItems, setItems] = React.useState(items);
  const [paymentLink, setPaymentLink] = React.useState(null);
  const generatingLinkRef = React.useRef(null);

  const onChange = (e) => {
    const { value } = e.target;
    const raw = value.replace(/,/g, "").replace("$", "").replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setInputValue(raw);
    const newAmount = parseFloat(raw);
    if(!isNaN(newAmount)) {
      setAmount(newAmount);
      setPaymentLink(null);
      generatingLinkRef.current = null;
      if(newAmount === originalAmount) setItems(items);
      else setItems([{desc: `${type.charAt(0).toUpperCase()}${type.substring(1)} Deposit`, amount: newAmount}]);
    } else {
      setAmount(0);
      setPaymentLink(null);
      generatingLinkRef.current = null;
    }
  }

  const generatePaymentLink = async () => {
    // Return cached link if it exists
    if(paymentLink) {
      return paymentLink;
    }
    
    // If already generating, wait for the existing promise
    if(generatingLinkRef.current) {
      return await generatingLinkRef.current;
    }
    
    if(amount < 1) {
      StateManager.setAlertAndOpen("The amount is too low to run a credit card.", "error");
      return null;
    }
    if((!customer.first_name || !customer.last_name) && !customer.customerName) {
      StateManager.setAlertAndOpen("The customer is missing information", "error");
      return null;
    }
    
    // Create and store the promise
    const linkPromise = (async () => {
      StateManager.setLoading(true);
      let cust_id = {}
      if(!customer.stripe_id){
        let cust_vars = filterCustomer(customer, ["email"]);
        cust_vars.name = customer.customerName ? customer.customerName : `${customer.first_name || ""} ${customer.last_name || ""}`;
        cust_vars.devtype = devtype;
        const stripe_cust = await RequestManager.post({function: "makeStripeCustomer", variables: {params: cust_vars}});
        console.log(stripe_cust)
        if(stripe_cust.success !== false) {
          if (constants.isValidCustomerId(customer.id)) await firebase.firestore().doc(`customers/${customer.id}`).update({stripe_id: stripe_cust.id});
          cust_id = {customer: stripe_cust.id}
        }
      }
      else{
        cust_id = {customer: customer.stripe_id}
      }

      let variables = {
        params: {
          amount: Math.floor(amount*100),
          ...cust_id,
          receipt_email: customer.email || null,
          metadata: {reference, type},
          description: `${reference} | ${title} | ${type}`,
          devtype,
        },
        options: {}
      }

      const intent = await RequestManager.post({function: "makePaymentIntent", variables});
      if(!intent.client_secret){
        StateManager.setLoading(false);
        generatingLinkRef.current = null;
        StateManager.setAlertAndOpen("Error generating link.  Try again.", "error");
        return null;
      }

      const descriptions = activeItems.map(item => item.desc).join(",");
      const charges = activeItems.map(item => item.amount).join(",");

      const link = encode(`${window.location.origin}/payment?intent=${intent.client_secret}&stock=${reference}&type=${type}&thumbnail=${thumbnail}&title=${title}&i=${descriptions}&c=${charges}`);
      setPaymentLink(link);
      generatingLinkRef.current = null;
      StateManager.setLoading(false);
      return link;
    })();
    
    generatingLinkRef.current = linkPromise;
    return await linkPromise;
  }

  const openLink = async () => {
    const link = await generatePaymentLink();
    if(link) {
      window.open(link, '_blank');
    }
  }

  const sendLink = async (shouldSend = false) => {
    console.log(customer)
    if(shouldSend) {
      if(!customer.email) {
        StateManager.setAlertAndOpen("The customer needs an email to send a link.", "error");
        return;
      }
      if(!customer.phone_number) {
        StateManager.setAlertAndOpen("The customer needs a phone number to send a link.", "error");
        return;
      }
    }

    const link = await generatePaymentLink();
    if(!link) return;
    
    const variables = {
      params: {
        name: customer.first_name || null,
        phone_number: customer.phone_number ? customer.phone_number.replace(/\D+/g, '') : null,
        reason: props.reason || `Please use the following link to pay your ${type} balance`,
        link: link,
        devtype,
      },
      options: {}
    };
    
    console.log(variables.params);
    console.log(shouldSend)
    
    if(shouldSend){
      StateManager.setLoading(true);
      const sms = await RequestManager.post({function: "sendPaymentLink", variables});
      StateManager.setLoading(false);
      if(sms.success !== false) StateManager.setAlertAndOpen("Link sent!", "success");
      else StateManager.setAlertAndOpen("Error sending link.  Try again.", "error");
    }
    else{
      await navigator.clipboard.writeText(link)
      StateManager.setAlertAndOpen("Link Copied!", "success");
    }
  }

  return (
    <div style={{
      backgroundColor: 'white', 
      padding: '25px', 
      width: '100%', 
      display: 'flex', 
      justifyContent: 'space-between',
      borderBottomWidth: '3px' 
    }}>
      <FormControlLabel control={props.removeCheck ? <></> : <Checkbox checked={false} />} label={label} />
      <div style={{maxHeight: '40px', display:'flex', flexDirection:'row'}}>
        {
          props.removeAmount ? null :
          <TextField 
            inputProps={{style: { textAlign: props.alignment || 'center'}}}
            style={{maxWidth: props.maxWidth || "40%", width:"50%", marginRight: 30}}
            {...props}
            id={props.id}
            label={""}
            placeholder="Amount"
            type={props.type}
            value={"$"+inputValue}
            onChange={onChange}
            disabled={enable ? !enable : amount === 0}
          />
        }
        <IconButton
          color="primary"
          onClick={openLink}
          disabled={amount < 1}
          title="Open Payment Link"
        >
          <OpenInNew />
        </IconButton>
        <IconButton
          color="primary"
          onClick={() => sendLink(false)}
          disabled={amount < 1}
          title="Copy Link"
        >
          <ContentCopy />
        </IconButton>
        <IconButton
          color="secondary"
          onClick={() => sendLink(true)}
          disabled={amount < 1}
          title="Send SMS"
        >
          <Sms />
        </IconButton>
      </div>
    </div>
  );
}


const filterCustomer = (cust, keysToKeep = []) => {
  let new_cust = Object.keys(cust).reduce((result, key) => {
    if (keysToKeep.includes(key) && !!cust[key]) {
      result[key] = cust[key];
    }
    return result;
  }, {});

  return new_cust;
}


const encode = (str) => {
  return str.replace(/ /g, "%20");
}
